import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Badge, Offcanvas, Tab, Tabs } from "react-bootstrap";
import TabAddPayment from "./tabs/TabAddPayment";
import TabPaymentsList from "./tabs/TabPaymentsList";
import { useTranslation } from "react-i18next";
import * as apiWarehouses from "api/apiWarehouses";
import { useToast } from "context/ToastContext";
import ESpinner from "../../others/Spinner";
import TabComingList from "./tabs/TabComingList";
import { useAuth } from "context/AuthContext";
import useSettingsParams from "hooks/useSettingsParams";
import { canWarehouseOps } from "utils/helpers/access/hasUserAccess";

interface Props {
    wId: number;
    showTitle: string | null;
    showId: number | null;
    setShowId: Dispatch<SetStateAction<number | null>>;
    fetchStocks: (force?: boolean) => void;
}

const OffPayments = ({ wId, showTitle, showId, setShowId, fetchStocks }: Props) => {
    const { t } = useTranslation(['Warehouses']);
    const { showToast } = useToast();
    const { auth } = useAuth();
    const params = useSettingsParams();
    const canOps = canWarehouseOps(auth?.user);
    const canCredit = Boolean(params.creditControl);

    // ✅ стан активної вкладки
    const [activeTab, setActiveTab] = useState<'payments' | 'add-payment'>('payments');
    const [payments, setPayments] = useState<Record<string, any>[]|null>(null);
    const [comings, setComings] = useState<Record<string, any>[]|null>(null);
    const [fetching, setFetching] = useState(false);
    const [error, setError] = useState<boolean>(false);
    const [wrongAmount, setWrongAmount] = useState<number>(0);
    const [recommendAmount, setRecommendAmount] = useState<number>(0);

    const fetchPayments = async () => {
        setFetching(true);
        setError(false);
        const resPayments = await apiWarehouses.getPayments(wId, showId);
        const resComing = await apiWarehouses.getOperations(wId, showId, {page:1, onPage:9999999999});
        setComings(resComing?.operations||null);
        if (!resPayments) {
            setError(true);
            setFetching(false);
            return showToast(t('common:messError'), 'danger');
        }
        setPayments(resPayments?.payments||null);
        const amountByPayments = resPayments?.payments?.reduce((acc, p) => acc + p.payment, 0);
        setWrongAmount(amountByPayments - (resPayments?.stock?.stockAmountPayment||0));
        setRecommendAmount(resPayments?.stock?.stockAmountAdd - resPayments?.stock?.stockAmountPayment);
        setFetching(false);
        setError(false);
    }

    const handleHide = () => {
        setShowId(null);
        setActiveTab('payments'); // опціонально: скидаємо на першу вкладку
    };

    useEffect(()=>{
        if (!canCredit) return;
        if (showId) fetchPayments();
    }, [showId, canCredit]);

    if (!canCredit) return null;

    return (
        <Offcanvas
            className="off-import"
            show={showId !== null}
            onHide={handleHide}
            placement="end">
        <Offcanvas.Header className="border-bottom" closeButton>
            <div className="d-flex justify-content-between w-100">
                <Offcanvas.Title>{showTitle}</Offcanvas.Title>
                {wrongAmount > 0 && (<div><Badge bg="danger">{t('wrongAmount')}</Badge></div>)}
            </div>
        </Offcanvas.Header>

        <Offcanvas.Body>
            {fetching ? <ESpinner/>
            :<Tabs
                variant="underline"
                id="off-payments-tab"
                className="b-3 invoice-tab border-bottom"
                activeKey={activeTab} 
                onSelect={(k) => k && setActiveTab(k as any)}>
                    
                <Tab
                    eventKey="payments"
                    title={<><i className="bi bi-cash-coin me-2 fs-14"></i>{t('tabs.payments')}</>}>
                    <TabPaymentsList 
                        payments={payments}
                        fetching={fetching}
                        error={error}
                        fetchPayments={fetchPayments}/> 
                </Tab>

                <Tab
                    eventKey="operations"
                    title={<><i className="bi bi-arrow-down-up me-2 fs-14"></i>{t('tabs.operations')}</>}>
                    <TabComingList 
                        comings={comings}
                        fetching={fetching}
                        error={error}
                        fetchPayments={fetchPayments}/>
                </Tab>

                {canOps &&
                (<Tab
                    eventKey="add-payment"
                    title={<><i className="bi bi-plus-circle me-2 fs-14"></i>{t('tabs.addPayment')}</>}>
                    <TabAddPayment 
                        recommendAmount={recommendAmount}
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                        wId={wId}
                        productId={showId}
                        fetchPayments={fetchPayments}
                        fetchStocks={fetchStocks}/>
                </Tab>)}
               
                </Tabs>}
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default OffPayments;
